/**
 * Plan detail — overview page for a single PlanCycle.
 *
 * Two modes based on input_type:
 *   Scenario A (item_pool | collection | provisional_plan | previous_carry_forward):
 *     Viability planning — no Plan of Record required. Activate as soon as a
 *     plan version exists. Show initiatives and direct links to sub-pages.
 *
 *   Scenario B (published_priority_list):
 *     POR workflow — requires an authorized Plan of Record before activation.
 *     Shows full POR governance actions.
 */

import { useEffect, useState, useCallback } from 'react';
import { Link, useParams } from 'react-router-dom';
import { COMMIT_TO_PLAN_VERSION_LABEL } from '../../lib/planning/vocabulary';
import {
  Play,
  Square,
  Loader2,
  Calendar,
  GitBranch,
  Users,
  Shield,
  AlertTriangle,
  GitCompare,
  ArrowRight,
  Clock,
  X,
  ClipboardList,
  BookOpen,
  ChevronDown,
  ChevronRight,
  ShieldCheck,
} from 'lucide-react';
import { getTypedSupabaseClient } from '../../lib/typedSupabaseClient';
import { useModuleGateForWorkspace } from '../../lib/moduleGate';
import { SignalLog } from '../../components/signals/SignalLog';
import {
  listSignalEvents,
  listUnansweredSignals,
  listItemTerms,
  type ItemTermEntry,
} from '../../lib/signalEventsService';
import { deriveSignalWatch } from '../../components/signals/signalWatch';
import type { SignalEventRow } from '../../types/signal-events';
import { PlanHealthPanel } from '../../components/planning/PlanHealthPanel';
import { PlanNarrativeSummaryCard } from '../../components/planning/PlanNarrativeSummaryCard';
import { usePlanNarrativeEnabled } from '../../hooks/usePlanNarrativeEnabled';
import { DivergencePanel } from '../../components/planning/DivergencePanel';
import { NextRightWorkPanel } from '../../components/planning/NextRightWorkPanel';
import { TradeoffLedger } from '../../components/planning/TradeoffLedger';
import { ScenarioComparisonPanel } from '../../components/planning/ScenarioComparisonPanel';
import { ReprioritizationTriggerPanel } from '../../components/planning/ReprioritizationTriggerPanel';
import {
  getPlanSnapshot,
  canActivatePlanCycle,
  activatePlanCycle,
  closePlanCycle,
  getUnfinishedInitiativesForClosure,
  listInitiativesForPlanVersion,
  listDivergencesForPlanVersion,
  listTradeoffsForPlanVersion,
  listPlanVersions,
  listReprioritizationTriggersForPlanCycle,
  listScenariosForPlanCycle,
  getItemTitlesByIds,
  getDecisionTitlesByIds,
  backfillInitiativeTitlesForPlanVersion,
  canPerformPlanningGovernance,
  runPlanVersionEvaluation,
  getCurrentPorSnapshot,
} from '../../lib/planningService';
import type {
  PorSnapshotRow,
  PlanCycle,
  Initiative,
  UnfinishedInitiativeForClosure,
  PlanClosureDecision,
  TradeoffDecision,
  ReprioritizationTriggerRow,
  PlanScenarioRow,
} from '../../lib/planningService';
import type { PlanVersion, RankSequenceDivergence } from '../../types/planning';
import { useItemHref } from '@/lib/itemLinkScope';
import { usePlanApprover, PLAN_CLOSE_AUTHORITY_LINE } from '@/hooks/prioritization/usePlanApprover';

// ─── Constants ────────────────────────────────────────────────────────────────

const POR_INPUT_TYPES = new Set(['published_priority_list']);

const INPUT_LABELS: Record<string, string> = {
  published_priority_list: 'Priority List',
  item_pool: 'Item Pool',
  collection: 'Collection',
  provisional_plan: 'Provisional',
  previous_carry_forward: 'Carry Forward',
};

const STATUS_STYLES: Record<string, { dot: string; text: string; label: string }> = {
  draft:      { dot: 'bg-[var(--mustard)]', text: 'text-[var(--mustard)]', label: 'Draft' },
  active:     { dot: 'bg-[var(--green)]',  text: 'text-[var(--green)] dark:text-[var(--text-ter)]',  label: 'Active' },
  closed:     { dot: 'bg-[var(--slate)]',   text: 'text-[var(--text-ter)]',   label: 'Closed' },
  superseded: { dot: 'bg-[var(--surface)]',   text: 'text-[var(--text-sec)]',   label: 'Superseded' },
};

// The masthead's present-state pill, derived from the signal watch — never a
// letter grade, always the watch's own language.
const WATCH_PILL: Record<string, { label: string; cls: string }> = {
  clear:    { label: 'holding',  cls: 'bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)]' },
  watching: { label: 'on watch', cls: 'bg-[var(--mustard-dim)] text-[var(--mustard)]' },
  overdue:  { label: 'overdue',  cls: 'bg-[var(--coral-dim)] text-[var(--coral)]' },
};

const fmtShort = (iso: string) =>
  new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });

// ─── Sub-page nav cards ───────────────────────────────────────────────────────

interface SubPage {
  label: string;
  description: string;
  href: string;
  icon: React.ElementType;
}

function subPages(base: string, narrativeUnified: boolean): SubPage[] {
  return [
    { label: 'Milestones',       description: 'Dates, at-risk indicators, and slip dependencies', href: `${base}/milestones`,       icon: Calendar },
    { label: 'Work Graph',       description: 'Work units, dependencies, and critical path',      href: `${base}/work-graph`,       icon: GitBranch },
    { label: 'Capacity',         description: 'Resource allocations by time window',              href: `${base}/capacity`,         icon: Users },
    { label: 'Governance',       description: 'Audit trail, versions, and POR authorization',     href: `${base}/governance`,       icon: Shield },
    { label: 'Assumptions & Risks', description: 'Tracked assumptions and risk register',         href: `${base}/assumptions-risks`, icon: AlertTriangle },
    { label: 'Changes',          description: 'Diff vs previous Plan of Record snapshot',         href: `${base}/changes`,          icon: GitCompare },
    // Unified PoR narrative surface (DEP-026) — chip renders only with the
    // plan_narrative gate on, same as the summary card and route.
    ...(narrativeUnified
      ? [{ label: 'Narrative', description: 'The written story behind the plan — sections, KPIs, proof points, risks, asks; frozen with POR authorization', href: `${base}/narrative`, icon: BookOpen }]
      : []),
  ];
}

// ─── Status badge ─────────────────────────────────────────────────────────────

function StatusBadge({ status }: { status: string }) {
  const s = STATUS_STYLES[status] ?? STATUS_STYLES.draft;
  // Say what is active. A bare "Active" beside a plan that has authorized no
  // version reads as "the plan of record is active", which is why this page and
  // the Governance page — "This plan has no plan of record yet" — appeared to
  // contradict each other. They were describing two different things: the plan,
  // and whether a version of it has been made the record.
  return (
    <span className={`inline-flex items-center gap-1.5 text-sm font-medium ${s.text}`}>
      <span className={`w-2 h-2 rounded-full ${s.dot}`} />
      <span className="text-[var(--text-ter)] font-normal">Plan</span>
      {s.label}
    </span>
  );
}

// ─── Close modal ─────────────────────────────────────────────────────────────

function CloseModal({
  cycleId,
  unfinished,
  itemTitles,
  onClose,
  onClosed,
}: {
  cycleId: string;
  unfinished: UnfinishedInitiativeForClosure[];
  itemTitles: Record<string, string>;
  onClose: () => void;
  onClosed: (cycle: PlanCycle) => void;
}) {
  const [decisions, setDecisions] = useState<Record<string, PlanClosureDecision>>(
    Object.fromEntries(unfinished.map((u) => [u.initiative_id, 'carry_forward'])),
  );
  const [closing, setClosing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleClose = async () => {
    setClosing(true);
    setError(null);
    const { data, error: err } = await closePlanCycle(cycleId, {
      decisions: unfinished.map((u) => ({
        initiative_id: u.initiative_id,
        item_id: u.item_id,
        decision: decisions[u.initiative_id] ?? 'carry_forward',
      })),
    });
    setClosing(false);
    if (err) { setError(err); return; }
    if (data) onClosed(data);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
      <div className="bg-[var(--bg-raised)] shadow-2xl border border-[var(--rule)]/80 w-full max-w-lg">
        <div className="flex items-center justify-between px-6 py-4 border-b border-[var(--rule)]">
          <h3 className="text-base font-semibold text-[var(--text-pri)]">Close plan</h3>
          <button type="button" onClick={onClose} className="p-1 rounded hover:bg-[var(--hover-tint)]">
            <X className="h-5 w-5 text-[var(--text-ter)]" />
          </button>
        </div>
        <div className="px-6 py-4 space-y-3">
          {unfinished.length > 0 ? (
            <>
              <p className="text-sm text-[var(--text-sec)]">Choose what happens to each unfinished initiative.</p>
              {unfinished.map((u) => (
                <div key={u.initiative_id} className="p-3 border border-[var(--rule)] space-y-2">
                  <p className="text-sm font-medium text-[var(--text-pri)]">
                    {itemTitles[u.item_id] || `Initiative ${u.item_id.slice(0, 8)}`} · {u.status}
                  </p>
                  <div className="flex gap-4">
                    {(['carry_forward', 'stop_recompose'] as PlanClosureDecision[]).map((d) => (
                      <label key={d} className="flex items-center gap-2 text-sm cursor-pointer">
                        <input type="radio" name={`d-${u.initiative_id}`} checked={decisions[u.initiative_id] === d}
                          onChange={() => setDecisions((p) => ({ ...p, [u.initiative_id]: d }))} />
                        {d === 'carry_forward' ? 'Carry forward' : 'Stop & recompose'}
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </>
          ) : (
            <p className="text-sm text-[var(--text-sec)]">All initiatives are complete. This plan will be closed.</p>
          )}
          {error && <p className="text-sm text-[var(--coral)]">{error}</p>}
        </div>
        <div className="flex justify-end gap-3 px-6 py-4 border-t border-[var(--rule)]">
          <button type="button" onClick={onClose} className="px-4 py-2 border border-[var(--rule)] text-sm font-medium text-[var(--text-sec)] hover:bg-[var(--surface)]">
            Cancel
          </button>
          <button type="button" onClick={() => void handleClose()} disabled={closing}
            className="px-4 py-2 bg-primary-600 text-white text-sm font-medium hover:bg-primary-700 disabled:opacity-50">
            {closing ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Close plan'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Instrument strip ─────────────────────────────────────────────────────────
//
// The record and its readings are distinct (owner ruling 2026-08-21; canon 23
// patch pending): this page IS the Plan of Record — stable between ceremonies —
// and it CARRIES an instrument panel. Instruments are live readings against
// the record (health, divergence, signal watch): quiet one-line rows by
// default, loud on exception, each expanding to its full workstation. They
// carry reading-time stamps, never version numbers — that temporal signature
// is the record/instrument boundary made visible.

type InstrumentTone = 'quiet' | 'warn' | 'alert' | 'idle';

const INSTRUMENT_DOT: Record<InstrumentTone, string> = {
  quiet: 'bg-[var(--green)]',
  warn: 'bg-[var(--mustard)]',
  alert: 'bg-[var(--coral)]',
  idle: 'bg-[var(--rule-bright)]',
};

function InstrumentRow({
  label,
  tone,
  reading,
  stamp,
  expanded,
  onToggle,
  children,
}: {
  label: string;
  tone: InstrumentTone;
  reading: string;
  stamp?: string;
  expanded: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}) {
  return (
    <div>
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center gap-3 px-5 py-2.5 text-left hover:bg-[var(--surface)] transition-colors"
      >
        <span className={`w-2 h-2 rounded-full flex-shrink-0 ${INSTRUMENT_DOT[tone]}`} />
        <span className="font-mono text-[10px] uppercase tracking-[0.14em] text-[var(--text-ter)] w-28 flex-shrink-0">
          {label}
        </span>
        <span
          className={`text-sm flex-1 truncate ${
            tone === 'alert' ? 'text-[var(--coral)] font-medium' : tone === 'warn' ? 'text-[var(--mustard)]' : 'text-[var(--text-sec)]'
          }`}
        >
          {reading}
        </span>
        {stamp && <span className="text-[11px] text-[var(--text-ter)] flex-shrink-0">{stamp}</span>}
        {expanded ? (
          <ChevronDown className="h-3.5 w-3.5 text-[var(--text-ter)] flex-shrink-0" />
        ) : (
          <ChevronRight className="h-3.5 w-3.5 text-[var(--text-ter)] flex-shrink-0" />
        )}
      </button>
      {expanded && <div className="px-5 pb-4 pt-1">{children}</div>}
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

export default function PlanningPlanDetail() {
  const itemHref = useItemHref();
  const { cycleId } = useParams<{ cycleId: string }>();
  const narrativeUnified = usePlanNarrativeEnabled();

  // Single snapshot row from por_snapshot_view replaces 3 parallel queries
  const [snapshot, setSnapshot] = useState<PorSnapshotRow | null>(null);
  const [initiatives, setInitiatives] = useState<Initiative[]>([]);
  const [divergences, setDivergences] = useState<RankSequenceDivergence[]>([]);
  const [tradeoffs, setTradeoffs] = useState<TradeoffDecision[]>([]);
  const [triggers, setTriggers] = useState<ReprioritizationTriggerRow[]>([]);
  const [scenarios, setScenarios] = useState<PlanScenarioRow[]>([]);
  const [planVersions, setPlanVersions] = useState<PlanVersion[]>([]);
  const [itemTitles, setItemTitles] = useState<Record<string, string>>({});
  const [itemTerms, setItemTerms] = useState<Record<string, ItemTermEntry[]>>({});
  const [versionDecisionTitles, setVersionDecisionTitles] = useState<Record<string, string>>({});
  const [workspaceName, setWorkspaceName] = useState<string | null>(null);

  // The charter sentence (plan_cycles.commitment_statement, migration 676).
  const [commitment, setCommitment] = useState<string | null>(null);
  const [editingCharter, setEditingCharter] = useState(false);
  const [charterDraft, setCharterDraft] = useState('');
  const [savingCharter, setSavingCharter] = useState(false);
  const [charterError, setCharterError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [activateAllowed, setActivateAllowed] = useState(false);
  const [activateBlockers, setActivateBlockers] = useState<string[]>([]);
  const [transitioning, setTransitioning] = useState(false);
  const [transitionError, setTransitionError] = useState<string | null>(null);

  const [showCloseModal, setShowCloseModal] = useState(false);
  // close_plan_cycle asks can_authorize_plan_of_record; draw Close plan only for
  // someone it will accept (build contract §2.8).
  const mayClose = usePlanApprover(cycleId);
  const [unfinishedForClosure, setUnfinishedForClosure] = useState<UnfinishedInitiativeForClosure[]>([]);
  const [runningEvaluation, setRunningEvaluation] = useState(false);

  // Instrument strip: which reading is expanded, plus the signal-watch feed
  // (workspace-wide; the strip row summarizes, the expanded SignalLog is the
  // workstation and fetches its own data).
  const [expandedInstrument, setExpandedInstrument] = useState<'health' | 'divergence' | 'signals' | null>(null);
  const [signalRows, setSignalRows] = useState<SignalEventRow[]>([]);
  const [signalUnanswered, setSignalUnanswered] = useState(0);
  const [showTradeoffs, setShowTradeoffs] = useState(false);
  const [showAllVersions, setShowAllVersions] = useState(false);
  /** The version the Plan of Record authorization points at, when there is one. */
  const [inForceVersionId, setInForceVersionId] = useState<string | null>(null);

  const isPorPlan = POR_INPUT_TYPES.has(snapshot?.input_type ?? '');
  const base = `/planning/plans/${cycleId}`;

  const load = useCallback(async () => {
    if (!cycleId) return;
    setLoading(true);
    setError(null);

    const { data: snap, error: snapErr } = await getPlanSnapshot(cycleId);
    if (snapErr) { setError(snapErr); setLoading(false); return; }
    if (!snap) { setError('Plan cycle not found.'); setLoading(false); return; }
    setSnapshot(snap);

    // Which version is authorized is a separate fact from which version this
    // page is showing; read it from the authorization record itself.
    void getCurrentPorSnapshot(cycleId).then(({ data }) =>
      setInForceVersionId(data?.plan_version_id ?? null),
    );

    const pvId = snap.plan_version_id ?? null;

    // Cycle-level queries (don't need pvId)
    const [triggersRes, scenariosRes, versionsRes, charterRes, wsRes] = await Promise.all([
      listReprioritizationTriggersForPlanCycle(cycleId),
      listScenariosForPlanCycle(cycleId),
      listPlanVersions(cycleId),
      getTypedSupabaseClient()
        .from('plan_cycles')
        .select('commitment_statement')
        .eq('id', cycleId)
        .maybeSingle(),
      snap.workspace_id
        ? getTypedSupabaseClient()
            .from('workspaces')
            .select('name')
            .eq('id', snap.workspace_id)
            .maybeSingle()
        : Promise.resolve({ data: null }),
    ]);
    setTriggers(triggersRes.data ?? []);
    setScenarios(scenariosRes.data ?? []);
    setPlanVersions(versionsRes.data ?? []);

    // Titles for the change log's "because" links — versions caused by a
    // change-course decision name that decision as their why.
    const sourceDecisionIds = [
      ...new Set(
        (versionsRes.data ?? [])
          .map((v) => v.source_decision_id ?? null)
          .filter((id): id is string => !!id)
      ),
    ];
    if (sourceDecisionIds.length > 0) {
      const { data: titles } = await getDecisionTitlesByIds(sourceDecisionIds);
      setVersionDecisionTitles(titles);
    }
    setCommitment(charterRes.data?.commitment_statement ?? null);
    setWorkspaceName(wsRes.data?.name ?? null);

    if (pvId) {
      const [initiativesRes, divergencesRes, tradeoffsRes] = await Promise.all([
        listInitiativesForPlanVersion(pvId),
        listDivergencesForPlanVersion(pvId),
        listTradeoffsForPlanVersion(pvId),
      ]);

      let list = initiativesRes.data ?? [];
      const needBackfill = list.some((i) => !i.title?.trim());
      if (needBackfill) {
        await backfillInitiativeTitlesForPlanVersion(pvId);
        const { data: refreshed } = await listInitiativesForPlanVersion(pvId);
        list = refreshed ?? list;
      }

      setInitiatives(list);
      setDivergences(divergencesRes.data ?? []);
      setTradeoffs(tradeoffsRes.data ?? []);

      // Fetch item titles — stored titles may be ID-based fallbacks from plans created
      // before item names were available. An item the plan holds reads the title it
      // authorized (1060); a later change stays out until the plan is amended.
      const allItemIds = [...new Set(list.map((i) => i.item_id).filter(Boolean))];
      if (allItemIds.length > 0) {
        const { data: titles } = await getItemTitlesByIds(allItemIds, cycleId);
        setItemTitles(titles);
        // Terms on the commitments — active decisions carrying pre-registered
        // expectations for these items. Absence degrades to no sub-line.
        if (snap.workspace_id) {
          try {
            setItemTerms(await listItemTerms(snap.workspace_id, allItemIds));
          } catch {
            setItemTerms({});
          }
        }
      }
    }

    setLoading(false);
  }, [cycleId]);

  useEffect(() => { void load(); }, [load]);

  // Signal-watch reading (only when the module is on and we know the workspace).
  // Gate on the PLAN's workspace, not the session's — demo mode / active tenant
  // can differ from the workspace this plan cycle belongs to.
  const workspaceId = snapshot?.workspace_id ?? null;
  const signalV2 = useModuleGateForWorkspace(workspaceId, 'signal_response');
  useEffect(() => {
    if (!signalV2 || !workspaceId) return;
    let cancelled = false;
    void Promise.all([
      listSignalEvents({ workspaceId }),
      listUnansweredSignals(workspaceId, {}),
    ])
      .then(([rows, unanswered]) => {
        if (cancelled) return;
        setSignalRows(rows);
        setSignalUnanswered(unanswered.length);
      })
      .catch(() => {
        /* the strip row degrades to idle; the workstation surfaces errors */
      });
    return () => { cancelled = true; };
  }, [signalV2, workspaceId, expandedInstrument]);

  // Materialize buckets + compute the constraint evaluation for the current
  // plan version — the Doc 15 gates POR authorization requires.
  const handleRunEngine = useCallback(async () => {
    const pvId = snapshot?.plan_version_id;
    if (!pvId || runningEvaluation) return;
    setRunningEvaluation(true);
    const { error: evalErr } = await runPlanVersionEvaluation(pvId);
    setRunningEvaluation(false);
    if (evalErr) { setError(evalErr); return; }
    await load();
  }, [snapshot?.plan_version_id, runningEvaluation, load]);

  // Gate check — re-runs when cycle status changes
  useEffect(() => {
    if (!cycleId || snapshot?.status !== 'draft') {
      setActivateAllowed(false);
      setActivateBlockers([]);
      return;
    }
    let cancelled = false;
    canActivatePlanCycle(cycleId).then(({ data, error: gateErr }) => {
      if (cancelled) return;
      if (gateErr) { setActivateAllowed(false); setActivateBlockers([gateErr]); return; }
      setActivateAllowed(data?.allowed ?? false);
      setActivateBlockers(data?.blockers ?? []);
    });
    return () => { cancelled = true; };
  }, [cycleId, snapshot?.status]);

  const handleActivate = async () => {
    if (!cycleId) return;
    setTransitionError(null);
    setTransitioning(true);
    const { error: err } = await activatePlanCycle(cycleId);
    setTransitioning(false);
    if (err) { setTransitionError(err); return; }
    // Reload the snapshot so status reflects the new state
    void load();
  };

  const handleClose = async () => {
    if (!cycleId) return;
    setTransitionError(null);
    const { data: unfinished, error: unfErr } = await getUnfinishedInitiativesForClosure(cycleId);
    if (unfErr) { setTransitionError(unfErr); return; }
    setUnfinishedForClosure(unfinished ?? []);
    setShowCloseModal(true);
  };

  // A plan with no version gets its first one from the engine (Doc 104
  // Steps 7→8: run → Propose). "Create draft" clones the plan
  // of record, and a plan with no version has none, so the old button here
  // (createDraftPlanVersion) always failed with "No active plan of record".

  const handleSaveCharter = async () => {
    if (!cycleId) return;
    setSavingCharter(true);
    setCharterError(null);
    const { error: err } = await getTypedSupabaseClient()
      .from('plan_cycles')
      .update({ commitment_statement: charterDraft.trim() || null })
      .eq('id', cycleId);
    setSavingCharter(false);
    if (err) { setCharterError(err.message); return; }
    setCommitment(charterDraft.trim() || null);
    setEditingCharter(false);
  };

  if (!cycleId) return null;

  if (loading) {
    return (
      <div className="flex items-center gap-2 px-6 py-10 text-[var(--text-ter)]">
        <Loader2 className="h-5 w-5 animate-spin" /> Loading plan…
      </div>
    );
  }

  if (error || !snapshot) {
    return (
      <div className="px-6 py-10">
        <Link to="/planning/plans?catalog=1" className="text-sm text-primary-600 hover:underline">← All plans</Link>
        <p className="mt-4 text-[var(--coral)]">{error ?? 'Plan cycle not found.'}</p>
      </div>
    );
  }

  const inputLabel = INPUT_LABELS[snapshot.input_type] ?? snapshot.input_type;
  const hasVersion = !!snapshot.plan_version_id;
  const hasPor = !!snapshot.snapshot_id;
  const evalSummary =
    snapshot.evaluation_summary as { pass?: number; warn?: number; fail?: number; total?: number } | null;
  // Older evaluations stored no `total` — derive it so the reading never
  // renders "3 of — checks failing".
  const evalTotal = evalSummary
    ? evalSummary.total ?? (evalSummary.pass ?? 0) + (evalSummary.warn ?? 0) + (evalSummary.fail ?? 0)
    : 0;
  const signalWatch = deriveSignalWatch(
    signalRows.filter((s) => s.status !== 'dismissed'),
    signalUnanswered
  );
  // Resolved divergences (accepted/mitigated/disputed) are ledger history, not
  // an open reading — only open ones drive the instrument.
  const openDivergences = divergences.filter(
    (d) => !['accepted', 'mitigated', 'disputed'].includes(d.resolution_status ?? 'detected')
  );
  const canGovern = canPerformPlanningGovernance(snapshot.workspace_id ?? null);

  // Ranked commitments split at the line: dropped initiatives are held below
  // it, with their why; everything else is funded above.
  const aboveLine = initiatives.filter((i) => (i.status ?? 'planned') !== 'dropped');
  const belowLine = initiatives.filter((i) => (i.status ?? 'planned') === 'dropped');

  // Masthead receipt: the earliest open answer-due date is the record's next check.
  const nextCheck = signalRows
    .filter((s) => s.status !== 'dismissed' && s.review_by)
    .map((s) => s.review_by as string)
    .sort()[0];
  const watchPill = WATCH_PILL[signalWatch.state];

  return (
    <div className="max-w-4xl space-y-6">
      {/* ── Masthead — the record's identity and its receipt ─────────────────
          Eyebrow names the record, the receipt says who ratified what and
          when, and the pill is the watch's present state. */}
      <div>
        <div className="font-mono text-[10px] tracking-[0.16em] uppercase text-[var(--text-ter)]">
          Plan of Record
          {workspaceName ? ` · ${workspaceName.replace(/\s+Workspace$/i, '')}` : ''}
        </div>
        <h1 className="font-heading text-2xl font-semibold text-[var(--text-pri)] mt-1">
          {snapshot.name || 'Untitled plan'}
        </h1>
        <div className="flex items-center flex-wrap gap-x-2 gap-y-1 mt-1.5 text-[13px] text-[var(--text-sec)]">
          {isPorPlan && hasPor ? (
            <>
              <span className="inline-flex items-center gap-1.5 font-semibold text-[var(--green)] dark:text-[var(--text-ter)]">
                <ShieldCheck className="h-4 w-4" />
                Ratified v{snapshot.version_number ?? '—'}
              </span>
              {snapshot.authorized_at && (
                <>
                  <span className="text-[var(--text-ter)]">·</span>
                  <span>
                    {new Date(snapshot.authorized_at).toLocaleDateString(undefined, {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric',
                    })}
                  </span>
                </>
              )}
              <span className="text-[var(--text-ter)]">·</span>
            </>
          ) : null}
          <StatusBadge status={snapshot.status} />
          <span className="text-[var(--text-ter)]">·</span>
          <span className="text-[var(--text-ter)]">{inputLabel}</span>
          {nextCheck && (
            <>
              <span className="text-[var(--text-ter)]">·</span>
              <span>
                Next check{' '}
                <span className="font-semibold text-[var(--text-pri)]">{fmtShort(nextCheck)}</span>
              </span>
            </>
          )}
          {signalV2 && (
            <span
              className={`font-mono text-[10px] uppercase tracking-[0.08em] px-2 py-0.5 rounded-full ${watchPill.cls}`}
            >
              {watchPill.label}
            </span>
          )}
        </div>
      </div>

      {/* ── The commitment — the charter sentence the ranked list serves ── */}
      {(commitment || canGovern) && (
        <div className="bg-[var(--bg-raised)] border border-[var(--rule)] border-l-[3px] border-l-[#D4A017] px-6 py-4">
          <div className="flex items-start justify-between gap-3">
            <span className="font-mono text-[10px] tracking-[0.16em] uppercase text-[var(--text-ter)]">
              The commitment
            </span>
            {canGovern && !editingCharter && commitment && (
              <button
                type="button"
                onClick={() => {
                  setCharterDraft(commitment ?? '');
                  setEditingCharter(true);
                }}
                className="text-xs text-[var(--text-ter)] hover:text-[var(--text-ter)]"
              >
                Edit
              </button>
            )}
          </div>
          {editingCharter ? (
            <div className="mt-2">
              <textarea
                value={charterDraft}
                onChange={(e) => setCharterDraft(e.target.value)}
                rows={3}
                placeholder="One sentence: what this plan commits to, and on what conditions."
                className="w-full font-serif text-[16px] leading-relaxed text-[var(--text-pri)] border border-[var(--rule)] px-3 py-2 focus:border-primary-300 focus:ring-2 focus:ring-primary-100 focus:outline-none resize-none"
                autoFocus
              />
              <div className="flex items-center gap-2 mt-2">
                <button
                  type="button"
                  disabled={savingCharter}
                  onClick={() => void handleSaveCharter()}
                  className="text-xs font-semibold px-3 py-1.5 bg-primary-700 text-white hover:bg-primary-800 disabled:opacity-50"
                >
                  {savingCharter ? 'Saving…' : 'Save'}
                </button>
                <button
                  type="button"
                  disabled={savingCharter}
                  onClick={() => {
                    setEditingCharter(false);
                    setCharterError(null);
                  }}
                  className="text-xs font-semibold px-3 py-1.5 border border-[var(--rule)] text-[var(--text-sec)] hover:bg-[var(--surface)]"
                >
                  Cancel
                </button>
                {charterError && <span className="text-xs text-[var(--coral)]">{charterError}</span>}
              </div>
            </div>
          ) : commitment ? (
            <p className="font-serif text-[17px] leading-relaxed text-[var(--text-pri)] mt-2">{commitment}</p>
          ) : (
            <button
              type="button"
              onClick={() => {
                setCharterDraft('');
                setEditingCharter(true);
              }}
              className="mt-2 text-left font-serif text-[15px] italic text-[var(--text-ter)] hover:text-[var(--text-sec)]"
            >
              Write the one-sentence commitment this ranked list serves…
            </button>
          )}
        </div>
      )}

      {/* Action bar */}
      {(snapshot.status === 'draft' || snapshot.status === 'active') && (
        <div className="bg-[var(--bg-raised)] border border-[var(--rule)] px-5 py-4">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div>
              {snapshot.status === 'draft' ? (
                <>
                  <p className="text-sm font-medium text-[var(--text-pri)]">
                    {activateAllowed ? 'Ready to activate' : 'Not ready to activate'}
                  </p>
                  {activateBlockers.length > 0 && (
                    <ul className="mt-1 space-y-0.5">
                      {activateBlockers.map((b) => (
                        <li key={b} className="text-xs text-[var(--mustard)] flex items-center gap-1.5">
                          <span className="w-1 h-1 rounded-full bg-[var(--mustard)] flex-shrink-0" />
                          {b}
                        </li>
                      ))}
                    </ul>
                  )}
                  {isPorPlan && !hasVersion && (
                    <p className="text-xs text-[var(--text-ter)] mt-1">
                      Run the planning engine and commit its result to create the first plan version, then authorize it as the plan of record before activating.
                    </p>
                  )}
                </>
              ) : (
                <p className="text-sm font-medium text-[var(--text-pri)]">This plan is active.</p>
              )}
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              {snapshot.status === 'draft' && (
                <>
                  {isPorPlan && !hasVersion ? (
                    <Link
                      to="/planning/engine/summary"
                      className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white text-sm font-medium hover:bg-primary-700"
                    >
                      <Play className="h-4 w-4" />
                      {COMMIT_TO_PLAN_VERSION_LABEL}
                    </Link>
                  ) : (
                    <button
                      type="button"
                      disabled={!activateAllowed || transitioning}
                      onClick={() => void handleActivate()}
                      title={activateBlockers.join(' ')}
                      className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white text-sm font-medium hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {transitioning ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}
                      Activate plan
                    </button>
                  )}
                </>
              )}
              {snapshot.status === 'active' && mayClose && canPerformPlanningGovernance(snapshot.workspace_id ?? null) && (
                <button
                  type="button"
                  onClick={() => void handleClose()}
                  className="inline-flex items-center gap-2 px-4 py-2 border border-[var(--rule)] text-sm font-medium text-[var(--text-sec)] hover:bg-[var(--surface)]"
                >
                  <Square className="h-4 w-4" />
                  Close plan
                </button>
              )}
            </div>
          </div>
          {snapshot.status === 'active' && mayClose === false && (
            <p className="mt-2 text-xs text-[var(--text-ter)]">{PLAN_CLOSE_AUTHORITY_LINE}</p>
          )}
          {transitionError && (
            <p className="mt-2 text-sm text-[var(--coral)]">{transitionError}</p>
          )}
        </div>
      )}

      {/* POR authorization prompt — a transition state, shown only while unauthorized */}
      {isPorPlan && !hasPor && (
        <div className="bg-[var(--bg-raised)] border border-[var(--rule)] px-5 py-4 space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-semibold text-[var(--text-pri)]">Plan of Record</h2>
            <span className="inline-flex items-center gap-1.5 text-xs font-medium text-[var(--mustard)] bg-[var(--mustard-dim)] px-2 py-1">
              <Clock className="h-3.5 w-3.5" /> Not yet authorized
            </span>
          </div>
          {hasVersion && (
            <p className="text-xs text-[var(--text-ter)]">A plan version exists. Go to <Link to={`${base}/governance`} className="text-primary-600 hover:underline">Governance</Link> to authorize a Plan of Record.</p>
          )}
        </div>
      )}

      {/* ── Instrument strip — live readings against the record ─────────────
          Quiet by default, loud on exception; each row expands to its
          workstation. Reading-time stamps, never version numbers. */}
      {hasVersion && (
        <div className="bg-[var(--bg-raised)] border border-[var(--rule)] divide-y divide-[var(--rule)] overflow-hidden">
          <div className="flex items-center justify-between px-5 pt-3 pb-2">
            <h2 className="text-xs font-semibold uppercase tracking-wide text-[var(--text-ter)]">Instruments</h2>
            <span className="text-[11px] text-[var(--text-ter)]">live readings · not part of the record</span>
          </div>

          <InstrumentRow
            label="Plan health"
            tone={
              !evalSummary ? 'idle'
                : (evalSummary.fail ?? 0) > 0 ? 'alert'
                  : (evalSummary.warn ?? 0) > 0 ? 'warn'
                    : 'quiet'
            }
            reading={
              !evalSummary
                ? 'Not checked yet — open to check'
                : (evalSummary.fail ?? 0) > 0
                  ? `${evalSummary.fail} of ${evalTotal} checks failing`
                  : (evalSummary.warn ?? 0) > 0
                    ? `Holds with ${evalSummary.warn} warning${(evalSummary.warn ?? 0) === 1 ? '' : 's'}`
                    : `Holds — ${evalSummary.pass ?? evalTotal} checks pass`
            }
            stamp={snapshot.evaluation_computed_at ? `as of ${new Date(snapshot.evaluation_computed_at).toLocaleString()}` : undefined}
            expanded={expandedInstrument === 'health'}
            onToggle={() => setExpandedInstrument((v) => (v === 'health' ? null : 'health'))}
          >
            <PlanHealthPanel
              evaluationStatus={snapshot.evaluation_status}
              evaluationSummary={evalSummary}
              evaluationDetails={
                snapshot.evaluation_details as { violations?: Array<{ check_key: string; result: string; severity?: string; explanation?: string; remediation?: string }> } | null
              }
              evaluationComputedAt={snapshot.evaluation_computed_at}
              evaluationId={snapshot.evaluation_id}
              planVersionId={snapshot.plan_version_id}
              openDivergenceCount={Number(snapshot.open_divergence_count ?? 0)}
              onRunEngine={runningEvaluation ? undefined : () => void handleRunEngine()}
              onEvaluationChanged={() => void load()}
              planBase={base}
            />
          </InstrumentRow>

          <InstrumentRow
            label="Divergence"
            tone={openDivergences.length > 0 ? 'alert' : 'quiet'}
            reading={openDivergences.length > 0 ? `${openDivergences.length} open — plan and reality disagree` : 'None open'}
            expanded={expandedInstrument === 'divergence'}
            onToggle={() => setExpandedInstrument((v) => (v === 'divergence' ? null : 'divergence'))}
          >
            {snapshot.plan_version_id && divergences.length > 0 ? (
              <DivergencePanel
                divergences={divergences}
                canResolve={false}
                onResolutionChange={() => void load()}
              />
            ) : (
              <p className="text-xs text-[var(--text-ter)]">No open divergences between the Plan of Record and observed execution.</p>
            )}
          </InstrumentRow>

        </div>
      )}

      {/* ── The watch — signals age toward answers; the loop closes here.
          Its own card (not an expander): the flagship screen shows the loop
          closing, not a chevron hiding it. */}
      {signalV2 && workspaceId && (
        <div className="bg-[var(--bg-raised)] border border-[var(--rule)] px-5 py-4">
          <SignalLog
            workspaceId={workspaceId}
            canAct
            replanCycleId={cycleId}
            planCycleId={cycleId}
            itemOptions={initiatives
              .filter((i) => i.item_id)
              .map((i) => ({
                id: i.item_id as string,
                title:
                  itemTitles[i.item_id as string] ||
                  i.title ||
                  `Item ${(i.item_id as string).slice(0, 8)}`,
              }))}
          />
        </div>
      )}

      {/* ── Ranked commitments — the record is a sentence, then a list ──────
          Each row carries its own terms: the expectation it was answered
          with, the check date, the decision it hangs on. Dropped initiatives
          are held below the line with their why. */}
      <div className="bg-[var(--bg-raised)] border border-[var(--rule)] overflow-hidden">
        <div className="flex items-baseline justify-between gap-3 px-5 pt-4 pb-2">
          <h2 className="text-sm font-semibold text-[var(--text-pri)]">Ranked commitments</h2>
          <span className="text-xs text-[var(--text-ter)]">
            each carries its own terms — the expectation, the check date, the why
          </span>
        </div>
        {initiatives.length === 0 ? (
          <p className="px-5 pb-4 text-sm text-[var(--text-ter)]">
            {hasVersion
              ? 'No initiatives in this plan version yet.'
              : 'No plan version exists. Activate the plan to begin.'}
          </p>
        ) : (
          <div className="pb-2">
            {aboveLine.map((i, idx) => {
              const status = i.status ?? 'planned';
              const statusLabel =
                status === 'in_progress' ? 'In progress'
                  : status === 'complete' ? 'Complete'
                    : 'Planned';
              const statusClass =
                status === 'complete' ? 'bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)]'
                  : status === 'in_progress' ? 'bg-[var(--slate-dim)] text-[var(--text-sec)]'
                    : 'bg-[var(--surface)] text-[var(--text-sec)]';
              const terms = i.item_id ? itemTerms[i.item_id] : undefined;
              const term = terms?.[0];
              const exp = term?.expectations[0];
              return (
                <div key={i.id} className="px-5 py-2.5 border-t border-[var(--rule)]">
                  <div className="flex items-center gap-3">
                    {/* Ordinal in the rendered order — stored positions can be
                        null, duplicated, or gapped; the rank a reader sees is
                        the row's place in the list. */}
                    <span className="w-6 flex-shrink-0 font-mono font-bold text-[13px] text-[#D4A017]">
                      {idx + 1}
                    </span>
                    <span className="text-sm font-medium text-[var(--text-pri)] truncate flex-1">
                      {i.item_id ? (
                        <Link
                          to={itemHref(i.item_id)}
                          className="hover:text-primary-700 hover:underline"
                        >
                          {itemTitles[i.item_id] || i.title || `Item ${i.item_id.slice(0, 8)}`}
                        </Link>
                      ) : (
                        i.title || `Item ${i.id.slice(0, 8)}`
                      )}
                    </span>
                    <span className={`text-xs px-2 py-0.5  font-medium ${statusClass}`}>
                      {statusLabel}
                    </span>
                  </div>
                  {term && (
                    <p className="mt-0.5 pl-9 text-xs text-[var(--text-ter)] truncate">
                      {exp ? (
                        <>
                          <span className="font-semibold text-[var(--text-sec)]">Expect:</span> {exp.text}
                          {exp.checkBy && (
                            <>
                              {' · '}
                              <span className="font-semibold text-[var(--text-sec)]">check</span>{' '}
                              {fmtShort(exp.checkBy)}
                            </>
                          )}
                        </>
                      ) : (
                        term.decisionTitle
                      )}
                      {' · '}
                      <Link
                        to={`/prioritization/decisions/${term.decisionId}`}
                        className="text-primary-700 hover:underline"
                      >
                        on the record
                      </Link>
                      {terms!.length > 1 && (
                        <span className="text-[var(--text-ter)]"> · +{terms!.length - 1} more</span>
                      )}
                    </p>
                  )}
                </div>
              );
            })}
            {belowLine.length > 0 && (
              <>
                <div className="flex items-center gap-2.5 px-5 py-2 font-mono text-[10px] uppercase tracking-[0.14em] text-[var(--text-ter)] whitespace-nowrap">
                  <span className="h-px flex-1 border-t border-dashed border-[var(--rule)]" />
                  the line — funded above, explicitly not below
                  <span className="h-px flex-1 border-t border-dashed border-[var(--rule)]" />
                </div>
                {belowLine.map((i, idx) => (
                  <div key={i.id} className="px-5 py-2.5 border-t border-[var(--rule)] opacity-50">
                    <div className="flex items-center gap-3">
                      <span className="w-6 flex-shrink-0 font-mono font-bold text-[13px] text-[var(--text-ter)]">
                        {aboveLine.length + idx + 1}
                      </span>
                      <span className="text-sm font-medium text-[var(--text-sec)] truncate flex-1">
                        {i.item_id ? (
                          <Link
                            to={itemHref(i.item_id)}
                            className="hover:text-primary-700 hover:underline"
                          >
                            {itemTitles[i.item_id] || i.title || `Item ${i.item_id.slice(0, 8)}`}
                          </Link>
                        ) : (
                          i.title || `Item ${i.id.slice(0, 8)}`
                        )}
                      </span>
                      <span className="text-xs px-2 py-0.5 font-medium bg-[var(--hover-tint)] text-[var(--text-ter)]">
                        Held below
                      </span>
                    </div>
                    {i.drop_reason && (
                      <p className="mt-0.5 pl-9 text-xs text-[var(--text-ter)]">
                        <span className="font-semibold text-[var(--text-sec)]">Why held:</span>{' '}
                        {i.drop_reason}
                      </p>
                    )}
                  </div>
                ))}
              </>
            )}
          </div>
        )}
      </div>

      {/* Next Right Work — the record's consequence: given the commitments and
          the readings, what follows now (Doc 17 §1.2) */}
      {snapshot.plan_version_id && (
        <NextRightWorkPanel planVersionId={snapshot.plan_version_id} planCycleId={cycleId} planBase={base} />
      )}

      {/* Reprioritization triggers — exception-only; the proactive propose-replan
          action lives on Governance */}
      {triggers.length > 0 && (
        <ReprioritizationTriggerPanel
          planCycleId={cycleId}
          triggers={triggers}
          onChanged={() => void load()}
        />
      )}

      {/* Tradeoff Ledger — record archaeology: a count line, expandable (Doc 17 §1.4) */}
      {tradeoffs.length > 0 && (
        <div className="bg-[var(--bg-raised)] border border-[var(--rule)]">
          <button
            type="button"
            onClick={() => setShowTradeoffs((v) => !v)}
            className="w-full flex items-center justify-between px-5 py-3 text-left hover:bg-[var(--surface)] transition-colors"
          >
            <span className="text-sm text-[var(--text-sec)]">
              {tradeoffs.length} tradeoff{tradeoffs.length === 1 ? '' : 's'} on record
            </span>
            {showTradeoffs ? (
              <ChevronDown className="h-3.5 w-3.5 text-[var(--text-ter)]" />
            ) : (
              <ChevronRight className="h-3.5 w-3.5 text-[var(--text-ter)]" />
            )}
          </button>
          {showTradeoffs && (
            <div className="px-5 pb-4">
              <TradeoffLedger decisions={tradeoffs} />
            </div>
          )}
        </div>
      )}

      {/* Scenario Comparison — pre-commitment confidence loop; once the plan is
          active it is history (Doc 17 §1.5) */}
      {snapshot.status === 'draft' && (
        <ScenarioComparisonPanel
          planCycleId={cycleId}
          scenarios={scenarios}
          onChanged={() => void load()}
        />
      )}

      {/* Change log — the record's memory: every version with its why.
          Newest first; the current version is marked. */}
      {planVersions.length > 0 && (
        <div className="bg-[var(--bg-raised)] border border-[var(--rule)]">
          <div className="flex items-baseline justify-between px-5 pt-4 pb-2">
            <h2 className="text-sm font-semibold text-[var(--text-pri)]">Change log</h2>
            <span className="text-xs text-[var(--text-ter)]">
              the record&apos;s memory — every change carries its why
            </span>
          </div>
          <ul className="divide-y divide-[var(--rule)] px-5 pb-3">
            {(showAllVersions
              ? [...planVersions].reverse()
              : [...planVersions].reverse().slice(0, 8)
            ).map((v) => (
              <li key={v.id} className="py-2 text-sm text-[var(--text-sec)]">
                <span className="font-semibold text-[var(--text-pri)]">{fmtShort(v.created_at)}</span>
                {' — '}
                <span className="font-mono text-xs font-bold text-[var(--text-sec)]">v{v.version_number}</span>
                {/* "In force" is the authorized version — the Plan of Record.
                    A later version that nobody has authorized is a working
                    draft, and is never called current. */}
                {v.id === inForceVersionId ? (
                  <span className="ml-1.5 text-[10px] font-mono uppercase tracking-wide text-[var(--green)] dark:text-[var(--text-ter)]">
                    in force
                  </span>
                ) : v.id === snapshot.plan_version_id ? (
                  <span className="ml-1.5 text-[10px] font-mono uppercase tracking-wide text-[var(--text-ter)]">
                    {inForceVersionId ? 'working draft · not authorized' : 'current'}
                  </span>
                ) : null}
                <span className="text-[var(--text-ter)]">
                  {' '}· {v.change_reason ? String(v.change_reason).replace(/_/g, ' ') : v.version_number === 1 ? 'initial version' : 'revised'}
                </span>
                {v.source_decision_id && (
                  <span className="text-[var(--text-ter)]">
                    {' '}— because{' '}
                    <Link
                      to={`/prioritization/decisions/${v.source_decision_id}`}
                      className="text-primary-700 hover:underline"
                    >
                      {versionDecisionTitles[v.source_decision_id] ?? 'a decision on the record'}
                    </Link>
                  </span>
                )}
              </li>
            ))}
            {planVersions.length > 8 && (
              <li className="py-2">
                <button
                  type="button"
                  onClick={() => setShowAllVersions((v) => !v)}
                  className="text-xs font-medium text-primary-700 hover:underline"
                >
                  {showAllVersions
                    ? 'Show recent only'
                    : `Show all ${planVersions.length} versions`}
                </button>
              </li>
            )}
          </ul>
        </div>
      )}

      {/* Sub-page navigation — one compact row; navigation costs a line, not a screen */}
      <div className="flex flex-wrap items-center gap-x-1 gap-y-1.5">
        <span className="text-xs font-semibold uppercase tracking-wide text-[var(--text-ter)] mr-2">Plan sections</span>
        {subPages(base, narrativeUnified).map((page) => (
          <Link
            key={page.href}
            to={page.href}
            title={page.description}
            className="inline-flex items-center gap-1.5 border border-[var(--rule)] bg-[var(--bg-raised)] px-3 py-1.5 text-xs font-medium text-[var(--text-sec)] hover:border-primary-200 hover:bg-primary-50/30 hover:text-primary-700 transition-all"
          >
            <page.icon className="h-3.5 w-3.5 text-[var(--text-ter)]" />
            {page.label}
          </Link>
        ))}
      </div>

      {/* Related surfaces — catalog + narrative instrument (folded in over time) */}
      <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-5">
        <h2 className="text-xs font-semibold uppercase tracking-wide text-[var(--text-ter)] mb-3">Related</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Link
            to="/planning/plans?catalog=1"
            className="group flex items-start gap-3 border border-[var(--rule)] px-4 py-3 hover:border-primary-200 hover:bg-primary-50/30 transition-all"
          >
            <ClipboardList className="h-4 w-4 text-[var(--text-ter)] group-hover:text-primary-500 shrink-0 mt-0.5" />
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-[var(--text-pri)]">All plans</p>
              <p className="text-xs text-[var(--text-ter)] mt-0.5">Browse plan cycles and set the workspace primary</p>
            </div>
            <ArrowRight className="h-3.5 w-3.5 text-[var(--text-ter)] group-hover:text-primary-400 shrink-0 mt-0.5" />
          </Link>
          {narrativeUnified && cycleId ? (
            <PlanNarrativeSummaryCard cycleId={cycleId} />
          ) : (
            <Link
              to="/planning/plan-of-record/catalog"
              className="group flex items-start gap-3 border border-[var(--rule)] px-4 py-3 hover:border-primary-200 hover:bg-primary-50/30 transition-all"
            >
              <BookOpen className="h-4 w-4 text-[var(--text-ter)] group-hover:text-primary-500 shrink-0 mt-0.5" />
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-[var(--text-pri)]">Narrative record</p>
                <p className="text-xs text-[var(--text-ter)] mt-0.5">Org narrative sections, condition band, and change log</p>
              </div>
              <ArrowRight className="h-3.5 w-3.5 text-[var(--text-ter)] group-hover:text-primary-400 shrink-0 mt-0.5" />
            </Link>
          )}
        </div>
      </div>

      {showCloseModal && cycleId && (
        <CloseModal
          cycleId={cycleId}
          unfinished={unfinishedForClosure}
          itemTitles={itemTitles}
          onClose={() => setShowCloseModal(false)}
          onClosed={() => { setShowCloseModal(false); void load(); }}
        />
      )}
    </div>
  );
}
